<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.giftCard.actions.email.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.send'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <div class="pt-4 text-left ">
      <o-form v-if="giftCard" ref="form">
        <o-field-group
          :label="$t('general.preset')"
          :name="$t('general.preset')"
          class="mb-4 block"
        >
          <segment-field v-model="preset" :options="[{ value: $t('general.recipient'), label: $t('general.recipient') }, { value: $t('general.purchaser'), label: $t('general.purchaser') }, { value: $t('general.other'), label: $t('general.other') }]" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.toName')"
          :name="$t('general.toName')"
          class="mb-4 block"
          rules="required"
        >
          <text-field v-model="toName" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.toEmail')"
          :name="$t('general.toEmail')"
          class="mb-4 block"
          rules="required|email"
        >
          <text-field v-model="toEmail" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.fromName')"
          :name="$t('general.fromName')"
          class="mb-4 block"
          rules="required"
        >
          <text-field v-model="fromName" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.message')"
          :name="$t('general.message')"
          class="mb-4 block"
          rules="required"
        >
          <text-area-field v-model="message" :status="status" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { TextField, TextAreaField, SegmentField } from '@/components/fields'

import type { GiftCard } from '~/_api/models/giftCard'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const giftCard = ref<GiftCard | null>(null)
const preset = ref('Recipient')
const toName = ref('')
const toEmail = ref('')
const fromName = ref('')
const message = ref('')

const info = computed(() => {
  return $i18n.t('components.giftCard.actions.email.info', { number: (giftCard.value as any)?.customNumber || (giftCard.value as any)?.number })
})

watch(preset, (newPreset) => {
  switch (newPreset) {
    case $i18n.t('general.recipient'):
      toName.value = (giftCard.value as any)?.customer?.displayName || ''
      toEmail.value = (giftCard.value as any)?.customer?.email || ''
      break

    case $i18n.t('general.purchaser'):
      toName.value = (giftCard.value as any)?.order?.customer.displayName || ''
      toEmail.value = (giftCard.value as any)?.order?.customer.email || ''
      break

    default:
      toName.value = ''
      toEmail.value = ''
      break
  }
})

const open = (card: GiftCard) => {
  giftCard.value = card

  preset.value = $i18n.t('general.recipient')
  toName.value = (giftCard.value as any)?.customer?.displayName || ''
  toEmail.value = (giftCard.value as any)?.customer?.email || ''
  fromName.value = (giftCard.value as any)?.gift?.from?.name || ''
  message.value = (giftCard.value as any)?.gift?.message || ''

  dialog.value.open()
}

const confirm = async () => {
  if (!giftCard.value) return

  emit('update:loading', true)

  try {
    const success = await $api.giftCard.email(giftCard.value, {
      fromName: fromName.value,
      toName: toName.value,
      toEmail: toEmail.value,
      message: message.value,
    })

    if (success) {
      emit('success')

      $notification.toast({
        title: $i18n.t('general.sentEmail'),
        text: $i18n.t('components.giftCard.actions.email.successInfo'),
        variant: 'success',
      })

      emit('update:loading', false)

      return true
    }
  } catch (e: any) {
    console.log(e)
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('components.giftCard.actions.email.errorInfo'),
    })
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
